// Customer Data Management System
// Handles customer data capture, storage, and notifications
// Deployment trigger note: no runtime behavior change.

// Temporarily disable EmailJS to fix deployment issues
import emailjs from '@emailjs/browser';

// Initialize EmailJS with working credentials from Contact page
const EMAILJS_CONFIG = {
  serviceId: 'service_hv67lgm', // Working service ID from Contact page
  templateId: 'template_customer_inquiry', // We'll create this new template
  publicKey: 'mrN3zgLhejYqGtqNk' // Working public key from Contact page
};

class CustomerDataManager {
  constructor() {
    // Local customer cache is initialized from browser storage.
    this.customers = JSON.parse(localStorage.getItem('makrana_customers') || '[]');
    this.initEmailJS();
  }

  initEmailJS() {
    try {
      emailjs.init(EMAILJS_CONFIG.publicKey);
      console.log('EmailJS initialized for CustomerDataManager');
    } catch (error) {
      console.log('EmailJS initialization failed:', error);
    }
  }

  // Save customer data and send notifications
  async saveCustomerData(customerData, inquiryType = 'general') {
    const timestamp = new Date().toISOString();
    const customerId = `CUS_${Date.now()}`;
    
    const customerRecord = {
      id: customerId,
      ...customerData,
      inquiryType,
      timestamp,
      status: 'new',
      source: 'ai_chatbot'
    };

    // Save to local storage
    this.customers.unshift(customerRecord);
    localStorage.setItem('makrana_customers', JSON.stringify(this.customers));

    // Send email notification (if configured)
    await this.sendEmailNotification(customerRecord);

    // Send WhatsApp notification (simulate for now)
    this.sendWhatsAppNotification(customerRecord);

    return customerRecord;
  }

  // Send email notification to admin
  async sendEmailNotification(customerData) {
    try {
      const templateParams = {
        to_name: 'Admin',
        from_name: customerData.name,
        from_email: 'chatbot@makrana-marble.com', // Placeholder as chatbot might not get email
        message: `
          New Chatbot Inquiry:
          Name: ${customerData.name}
          Mobile: ${customerData.mobile}
          Type: ${customerData.inquiryType}
          Project: ${customerData.projectDetails || 'N/A'}
          Budget: ${customerData.estimatedBudget || 'N/A'}
        `
      };

      await emailjs.send(EMAILJS_CONFIG.serviceId, EMAILJS_CONFIG.templateId, templateParams);
      console.log('Email notification sent for:', customerData.name);
      return true;
    } catch (error) {
      console.error('Email notification failed:', error);
      this.savePendingNotification(customerData, 'email');
      return false;
    }
  }

  // Send WhatsApp notification (using WhatsApp API or Business API)
  sendWhatsAppNotification(customerData) {
    const message = `🔔 *New Customer Inquiry - Makrana Marble*

👤 *Customer:* ${customerData.name}
📱 *Mobile:* ${customerData.mobile}
🕐 *Time:* ${new Date(customerData.timestamp).toLocaleString()}
📋 *Type:* ${customerData.inquiryType}
🆔 *ID:* ${customerData.id}

${customerData.projectDetails ? `📝 *Project:* ${customerData.projectDetails}` : ''}
${customerData.estimatedBudget ? `💰 *Budget:* ₹${customerData.estimatedBudget}` : ''}

Reply to connect with the customer!`;

    // For now, we'll create a WhatsApp link that you can click to send to yourself
    const whatsappUrl = `https://wa.me/917014351132?text=${encodeURIComponent(message)}`;
    
    // Store notification for admin access
    const notifications = JSON.parse(localStorage.getItem('makrana_notifications') || '[]');
    notifications.unshift({
      id: `NOTIF_${Date.now()}`,
      type: 'whatsapp',
      customerData,
      message,
      whatsappUrl,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });
    localStorage.setItem('makrana_notifications', JSON.stringify(notifications));

    console.log('WhatsApp notification ready:', whatsappUrl);
    return whatsappUrl;
  }

  // Save failed notifications for retry
  savePendingNotification(customerData, type) {
    const pending = JSON.parse(localStorage.getItem('makrana_pending_notifications') || '[]');
    pending.unshift({
      id: `PENDING_${Date.now()}`,
      type,
      customerData,
      timestamp: new Date().toISOString(),
      retries: 0
    });
    localStorage.setItem('makrana_pending_notifications', JSON.stringify(pending));
  }

  // Get all customers for admin view
  getAllCustomers() {
    return this.customers;
  }

  // Get recent customers
  getRecentCustomers(limit = 10) {
    return this.customers.slice(0, limit);
  }

  // Get customers by status
  getCustomersByStatus(status) {
    return this.customers.filter(customer => customer.status === status);
  }

  // Update customer status
  updateCustomerStatus(customerId, status, notes = '') {
    const customerIndex = this.customers.findIndex(c => c.id === customerId);
    if (customerIndex !== -1) {
      this.customers[customerIndex].status = status;
      this.customers[customerIndex].lastUpdated = new Date().toISOString();
      if (notes) {
        this.customers[customerIndex].notes = notes;
      }
      localStorage.setItem('makrana_customers', JSON.stringify(this.customers));
      return true;
    }
    return false;
  }

  // Export customers to CSV
  exportCustomersCSV() {
    const headers = ['ID', 'Name', 'Mobile', 'Inquiry Type', 'Timestamp', 'Status', 'Project Details', 'Budget'];
    const csvData = [
      headers.join(','),
      ...this.customers.map(customer => [
        customer.id,
        customer.name,
        customer.mobile,
        customer.inquiryType,
        new Date(customer.timestamp).toLocaleString(),
        customer.status,
        (customer.projectDetails || '').replace(/,/g, ';'),
        customer.estimatedBudget || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `makrana_customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Get notification count
  getUnreadNotificationCount() {
    const notifications = JSON.parse(localStorage.getItem('makrana_notifications') || '[]');
    return notifications.filter(n => n.status === 'pending').length;
  }

  // Mark notifications as read
  markNotificationsRead() {
    const notifications = JSON.parse(localStorage.getItem('makrana_notifications') || '[]');
    notifications.forEach(n => n.status = 'read');
    localStorage.setItem('makrana_notifications', JSON.stringify(notifications));
  }
}

// Create global instance
const customerDataManager = new CustomerDataManager();

export default customerDataManager;

// Helper functions for easy integration
export const saveCustomerInquiry = async (customerData, inquiryType) => {
  return await customerDataManager.saveCustomerData(customerData, inquiryType);
};

export const getCustomerStats = () => {
  const customers = customerDataManager.getAllCustomers();
  const today = new Date().toDateString();
  const thisWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  
  return {
    total: customers.length,
    today: customers.filter(c => new Date(c.timestamp).toDateString() === today).length,
    thisWeek: customers.filter(c => new Date(c.timestamp) >= thisWeek).length,
    new: customers.filter(c => c.status === 'new').length,
    contacted: customers.filter(c => c.status === 'contacted').length,
    converted: customers.filter(c => c.status === 'converted').length
  };
};
